import styled from "@emotion/styled";
import { Flex } from "@radix-ui/themes";
import { observer } from "mobx-react-lite";
import { Toast as RadixToast } from "radix-ui";
import type React from "react";

import { media } from "@themes/breakpoints";
import { theme } from "@themes/themes";

import { useStores } from "@stores";

import { Button, Text } from "@src/ui-kit";

import { Icon } from "./Icon";

type ToastType = "success" | "error" | "info" | "warning";

export const ToastProvider: React.FC<{ children: React.ReactNode }> = observer(({ children }) => {
  const { toastStore } = useStores();

  return (
    <>
      {children}
      <RadixToast.Provider swipeDirection="right">
        {toastStore.toasts.map((toast) => (
          <ToastRoot
            key={toast.id}
            duration={toast?.duration ?? 5000}
            open={!toast.isClosing}
            onOpenChange={(open) => {
              if (!open) toastStore.close(toast.id);
            }}
          >
            <ToastBaseContainer data-type={toast.type}>
              <Header>
                <Flex align="center" flexGrow="1" gap="4px">
                  <Icon color={getIconColor(toast.type)} icon={getIcon(toast.type)} size={16} />
                  <Text color={getIconColor(toast.type)} font="body16">
                    {toast.title}
                  </Text>
                </Flex>
                <CloseToastButton
                  aria-label="Close notification"
                  type="button"
                  onClick={() => toastStore.close(toast.id)}
                >
                  <Icon color="accent-contrast" icon="ri-close-line" size={24} />
                </CloseToastButton>
              </Header>
              <Content>
                <Text color="accent-contrast" font="body16">
                  {toast.content}
                </Text>
              </Content>
              {toast.button && (
                <Footer>
                  <Button size="2" fullwidth onClick={toast.button.onClick}>
                    {toast.button.label}
                  </Button>
                </Footer>
              )}
            </ToastBaseContainer>
          </ToastRoot>
        ))}
        <ToastViewport />
      </RadixToast.Provider>
    </>
  );
});

const getIcon = (type?: ToastType) => {
  switch (type) {
    case "error":
      return "ri-information-line";
    case "info":
      return "ri-information-line";
    case "warning":
      return "ri-information-line";
    default:
      return "ri-check-line";
  }
};

const getIconColor = (type?: ToastType) => {
  switch (type) {
    case "error":
      return "red-strong";
    case "info":
      return "accent-9";
    case "warning":
      return "accent-9";
    default:
      return "green-strong";
  }
};

const ToastViewport = styled(RadixToast.Viewport)`
  position: fixed;
  bottom: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 10px;
  max-width: 100vw;
  margin: 0;
  list-style: none;
  z-index: 9999999;
  outline: none;
  padding: 24px;

  ${media.mobile} {
    padding: 12px 8px;
    margin-bottom: 72px;
    left: 0;
    right: 0;
  }
`;

const ToastRoot = styled(RadixToast.Root)`
  position: relative;
  will-change: transform, opacity;
  transition:
    transform 200ms cubic-bezier(0.16, 1, 0.3, 1),
    opacity 200ms cubic-bezier(0.16, 1, 0.3, 1);

  &[data-state="open"] {
    animation: slideIn 320ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  &[data-state="closed"] {
    animation: hide 180ms ease-in;
  }
  &[data-swipe="move"] {
    transform: translateX(var(--radix-toast-swipe-move-x));
  }
  &[data-swipe="cancel"] {
    transform: translateX(0);
    transition: transform 200ms ease-out;
  }
  &[data-swipe="end"] {
    animation: swipeOut 100ms ease-out;
  }

  @keyframes hide {
    from {
      opacity: 1;
      transform: translateX(0);
    }
    to {
      opacity: 0;
      transform: translateX(40px);
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(40px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes swipeOut {
    from {
      opacity: 1;
      transform: translateX(var(--radix-toast-swipe-end-x));
    }
    to {
      opacity: 0;
      transform: translateX(100%);
    }
  }
`;

const ToastBaseContainer = styled(Flex)`
  width: 300px;
  border-radius: 8px;
  flex-direction: column;
  background-color: ${theme.color("gray-4")};
  border: 1px solid ${theme.color("gray-4")};
  position: relative;
  overflow: hidden;

  ${media.mobile} {
    width: 100%;
    max-width: calc(100vw - 16px);
  }

  &::before {
    content: "";
    top: 0;
    left: 0;
    transform: translate(-50%, -50%);
    position: absolute;
    width: 212px;
    height: 212px;
    pointer-events: none;
    z-index: 0;
  }

  &[data-type="success"]::before {
    background: radial-gradient(50% 50% at 50% 50%, rgba(0, 237, 81, 0.12) 0%, rgba(0, 237, 123, 0) 100%);
  }
  &[data-type="error"]::before {
    background: radial-gradient(50% 50% at 50% 50%, rgba(240, 66, 72, 0.13) 0%, rgba(240, 66, 72, 0) 100%);
  }
  &[data-type="warning"]::before,
  &[data-type="info"]::before {
    background: radial-gradient(50% 50% at 50% 50%, rgba(226, 255, 4, 0.13) 0%, rgba(226, 255, 4, 0) 100%);
  }
`;

const Header = styled(Flex)`
  border-bottom: 1px solid ${theme.color("gray-5")};

  padding: 8px 12px;
`;

const CloseToastButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Content = styled(Flex)`
  padding: 12px 16px;
  word-break: break-word;
`;

const Footer = styled(Flex)`
  padding: 12px 16px;

  border-top: 1px solid ${theme.color("gray-5")};
`;
